// Account settings — the logged-in user manages their own record.
//   - Profile: full name, display name, title, phone (email + roles are
//     read-only; email is the login identity, roles are admin-managed).
//   - Password: change current → new (verifies current, 12-char min, and
//     signs out other devices).
//   - Sessions: sign out of every device.
//
// Staff-facing surface, so it ships at the internal cadence (no public
// design-review gate). Backed by /auth/profile + /auth/change-password +
// /auth/logout-everywhere.
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";
import { TwoStepSettings } from "@/components/TwoStepSettings";
import { roleWords } from "@/lib/roles";

interface Profile {
  id: string;
  email: string;
  fullName: string;
  displayName: string | null;
  title: string | null;
  phone: string | null;
  roles: string[];
}

type Msg = { kind: "success" | "error"; text: string } | null;

const ADMIN_LINKS = [
  { to: "/command/dashboard", label: "Admin dashboard" },
  { to: "/admin/catalog/products", label: "Products" },
  { to: "/admin/vendors", label: "Vendors" },
  { to: "/admin/categories", label: "Categories" },
  { to: "/admin/colors", label: "Colors" },
  { to: "/admin/access", label: "Access (who can sign in)" },
] as const;

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-brown-100 bg-white p-6">
      <h2 className="font-display text-lg font-bold text-brown-500">{title}</h2>
      {description && (
        <p className="mt-1 text-sm text-brown-300">{description}</p>
      )}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-1.5 block text-sm font-semibold text-brown-500"
      >
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-brown-300">{hint}</p>}
    </div>
  );
}

function Notice({ msg }: { msg: Msg }) {
  if (!msg) return null;
  const cls =
    msg.kind === "error"
      ? "border-rust-300 bg-rust-50 text-rust-600"
      : "border-peach-400 bg-peach-50 text-brown-500";
  return (
    <div
      role={msg.kind === "error" ? "alert" : "status"}
      className={`rounded-md border px-3 py-2 text-sm ${cls}`}
    >
      {msg.text}
    </div>
  );
}

export function SettingsPage() {
  const { user, patchUser, logout } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;

  // ── Profile ──────────────────────────────────────────────────────────
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [fullName, setFullName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [title, setTitle] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<Msg>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ profile: Profile }>("/auth/profile");
        if (cancelled) return;
        setProfile(res.profile);
        setFullName(res.profile.fullName);
        setDisplayName(res.profile.displayName ?? "");
        setTitle(res.profile.title ?? "");
        setPhone(res.profile.phone ?? "");
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const profileDirty =
    profile != null &&
    (fullName.trim() !== profile.fullName ||
      displayName.trim() !== (profile.displayName ?? "") ||
      title.trim() !== (profile.title ?? "") ||
      phone.trim() !== (profile.phone ?? ""));

  const handleProfileSave = async (e: FormEvent) => {
    e.preventDefault();
    setProfileMsg(null);
    if (!fullName.trim()) {
      setProfileMsg({ kind: "error", text: "Name can't be empty." });
      return;
    }
    setSavingProfile(true);
    try {
      const res = await api<{ profile: Profile }>("/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({
          full_name: fullName.trim(),
          display_name: displayName.trim(),
          title: title.trim(),
          phone: phone.trim(),
        }),
      });
      setProfile(res.profile);
      setFullName(res.profile.fullName);
      setDisplayName(res.profile.displayName ?? "");
      setTitle(res.profile.title ?? "");
      setPhone(res.profile.phone ?? "");
      patchUser({ name: res.profile.fullName });
      setProfileMsg({ kind: "success", text: "Profile saved." });
    } catch {
      setProfileMsg({
        kind: "error",
        text: "Couldn't save your profile. Try again in a moment.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Password ─────────────────────────────────────────────────────────
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<Msg>(null);

  const handlePwSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 12) {
      setPwMsg({
        kind: "error",
        text: "New password must be at least 12 characters.",
      });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({
        kind: "error",
        text: "New password and confirmation don't match.",
      });
      return;
    }
    if (newPw === currentPw) {
      setPwMsg({
        kind: "error",
        text: "New password must be different from your current one.",
      });
      return;
    }
    setSavingPw(true);
    try {
      await api("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPw,
          new_password: newPw,
        }),
      });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setPwMsg({
        kind: "success",
        text: "Password updated. You've been signed out on all other devices.",
      });
    } catch (err) {
      let text = "Couldn't change your password. Try again in a moment.";
      if (err instanceof ApiError && err.status === 400) {
        const code = (err.body as { error?: string } | null)?.error;
        if (code === "current_password_incorrect") {
          text = "Your current password is incorrect.";
        } else if (code === "same_password") {
          text = "New password must be different from your current one.";
        } else if (code === "password_too_short") {
          text = "New password must be at least 12 characters.";
        }
      }
      setPwMsg({ kind: "error", text });
    } finally {
      setSavingPw(false);
    }
  };

  // ── Sessions ─────────────────────────────────────────────────────────
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOutAll = async () => {
    setSigningOut(true);
    try {
      await api("/auth/logout-everywhere", { method: "POST" });
    } catch {
      // Soft-fail: our session is being torn down regardless.
    } finally {
      // logout-everywhere revokes this device's session too — bounce to
      // login so the state is predictable rather than expiring silently.
      await logout();
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display text-2xl font-bold text-brown-500">
        Settings
      </h1>
      <p className="mt-1 text-sm text-brown-300">
        Manage your profile, password and two-step sign-in.
      </p>

      <div className="mt-8 space-y-6">
        {/* Admin — reachable from Settings, no separate link to remember
            (Stephanie 2026-10-26). Admins only. */}
        {isAdmin && (
          <Card title="Admin" description="Products, vendors, categories, colors and who can sign in.">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ADMIN_LINKS.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className="rounded-md border border-brown-100 px-4 py-3 text-sm font-semibold text-brown-500 transition-colors hover:bg-cream-100"
                >
                  {a.label}
                </Link>
              ))}
            </div>
          </Card>
        )}
        {/* Profile */}
        <Card
          title="Profile"
          description="Your name and contact details. Email and roles are managed by an admin."
        >
          {loadFailed ? (
            <Notice
              msg={{
                kind: "error",
                text: "Couldn't load your profile. Refresh the page to try again.",
              }}
            />
          ) : profile == null ? (
            <p className="text-sm text-brown-300">Loading…</p>
          ) : (
            <form onSubmit={handleProfileSave} className="space-y-4">
              <Field label="Full name" htmlFor="full_name">
                <Input
                  id="full_name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  maxLength={200}
                  required
                />
              </Field>
              <Field
                label="Display name"
                htmlFor="display_name"
                hint="Optional — a shorter name shown around the app. Defaults to your full name."
              >
                <Input
                  id="display_name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={200}
                  placeholder="Optional"
                />
              </Field>
              <Field label="Title" htmlFor="title">
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Account Manager"
                />
              </Field>
              <Field label="Phone" htmlFor="phone" hint="Shown in your email signature — the main line plus your own extension, e.g. 877.538.5982 x123">
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={200}
                  placeholder="Optional"
                />
              </Field>

              <Field label="Email" htmlFor="email">
                <Input id="email" value={profile.email} disabled readOnly />
              </Field>
              <div>
                <span className="mb-1.5 block text-sm font-semibold text-brown-500">
                  Roles
                </span>
                <div className="flex flex-wrap gap-2">
                  {profile.roles.length === 0 ? (
                    <span className="text-sm text-brown-300">None assigned</span>
                  ) : (
                    // Plain names (Stephanie 2026-10-26): Sales, Production, …
                    roleWords(profile.roles).split(" · ").filter(Boolean).map((r) => (
                      <span
                        key={r}
                        className="rounded-full border border-peach-400 bg-peach-50 px-2.5 py-0.5 text-xs font-semibold text-brown-500"
                      >
                        {r}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <Notice msg={profileMsg} />

              <div className="flex items-center gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={savingProfile || !profileDirty}
                >
                  {savingProfile ? "Saving…" : "Save changes"}
                </Button>
                {!profileDirty && !savingProfile && (
                  <span className="text-xs text-muted">No changes</span>
                )}
              </div>
            </form>
          )}
        </Card>

        {/* Password */}
        <Card
          title="Password"
          description="Changing your password signs you out on every other device."
        >
          <form onSubmit={handlePwSubmit} className="space-y-4">
            <Field label="Current password" htmlFor="current_password">
              <Input
                id="current_password"
                type="password"
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
              />
            </Field>
            <Field
              label="New password"
              htmlFor="new_password"
              hint="At least 12 characters."
            >
              <Input
                id="new_password"
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
              />
            </Field>
            <Field label="Confirm new password" htmlFor="confirm_password">
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
              />
            </Field>

            <Notice msg={pwMsg} />

            <div className="pt-1">
              <Button
                type="submit"
                disabled={savingPw || !currentPw || !newPw || !confirmPw}
              >
                {savingPw ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Two-step sign-in (2026-10-21) */}
        <Card
          title="Two-step sign-in"
          description="A 6-digit code from your phone's authenticator app, on top of your password."
        >
          <TwoStepSettings />
        </Card>

        {/* Sessions */}
        <Card
          title="Sessions"
          description="Sign out everywhere you're logged in, including this device."
        >
          {confirmingSignOut ? (
            <div className="space-y-3">
              <p className="text-sm text-brown-400">
                This signs you out on every device, including this one. You'll
                need to sign in again.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={handleSignOutAll}
                  disabled={signingOut}
                >
                  {signingOut ? "Signing out…" : "Yes, sign out everywhere"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingSignOut(false)}
                  disabled={signingOut}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setConfirmingSignOut(true)}
            >
              Sign out of all devices
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}
